import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { BACKEND_URL } from '../lib/backendUrl';
import { CodeIcon, MicIcon, WrenchIcon, BookIcon, TrophyIcon, TargetIcon, CloseIcon } from './Icons';
import { uploadImageToCdn } from '../lib/uploadImage';
import '../styles/EventEdit.css';

type EventType = 'event' | 'workshop' | 'exhibition' | 'seminar' | 'competition' | 'hackathon' | 'conference' | 'other';

interface Timeline {
  phase: string;
  date: string;
  description: string;
}

interface CustomField {
  headline: string;
  value: string;
}

interface EventDetails {
  location?: string;
  startDate?: string;
  endDate?: string;
  registrationDeadline?: string;
  maxParticipants?: number;
  organizersContactNo?: string;
  organizersEmail?: string;
  eligibility?: string;
  tags?: string[];
  teamSize?: number;
  prizePool?: string;
  timeline?: Timeline[];
  customFields?: CustomField[];
}

interface EventEditFormData {
  title: string;
  description: string;
  eventType: EventType;
  clubId: string;
  clubName: string;
  location: string;
  image: string;
  startDate: string;
  endDate: string;
  registrationDeadline: string;
  maxParticipants: number;
  organizersContactNo: string;
  organizersEmail: string;
  eligibility: string;
  tags: string[];
  teamSize: number;
  prizePool: string;
  timeline: Timeline[];
  customFields: CustomField[];
}

interface Event {
  id: string;
  title: string;
  name?: string;
  description: string;
  eventType: EventType;
  type?: EventType;
  clubId: string;
  club_id?: string;
  clubName?: string;
  club_name?: string;
  location: string;
  image?: string;
  startDate: string;
  endDate: string;
  registrationDeadline: string;
  maxParticipants: number;
  fee?: number;
  organizersContactNo: string;
  organizersEmail: string;
  eligibility: string;
  tags?: string[];
  teamSize?: number;
  prizePool?: string;
  timeline?: Timeline[];
  details?: string;
  created_by?: string;
}

const EventEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { eventId } = useParams();
  const token = localStorage.getItem('token');
  const API_URL = BACKEND_URL;
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<Event | null>(null);
  const [selectedEventType, setSelectedEventType] = useState<EventType>('hackathon');

  const [formData, setFormData] = useState<EventEditFormData>({
    title: '',
    description: '',
    eventType: 'hackathon' as EventType,
    clubId: '',
    clubName: '',
    location: '',
    image: '',
    startDate: '',
    endDate: '',
    registrationDeadline: '',
    maxParticipants: 100,
    organizersContactNo: '',
    organizersEmail: '',
    eligibility: '',
    tags: [] as string[],
    teamSize: 4,
    prizePool: '',
    timeline: [] as Timeline[],
    customFields: [] as CustomField[],
  });

  const [imagePreview, setImagePreview] = useState('');
  const [newTag, setNewTag] = useState('');
  const [timelinePhase, setTimelinePhase] = useState({
    phase: '',
    date: '',
    description: ''
  });
  const [draggedTimelineIndex, setDraggedTimelineIndex] = useState<number | null>(null);
  const [customFieldDraft, setCustomFieldDraft] = useState<CustomField>({ headline: '', value: '' });

  const toDateTimeLocal = (value?: string): string => {
    if (!value) return '';
    const normalized = value.replace(' ', 'T');
    if (normalized.length >= 16) return normalized.slice(0, 16);
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 16);
  };

  const normalizeEventType = (value?: string): EventType => {
    const allowed: EventType[] = ['event', 'workshop', 'exhibition', 'seminar', 'competition', 'hackathon', 'conference', 'other'];
    return allowed.includes((value || '') as EventType) ? (value as EventType) : 'other';
  };

  const parseDetails = (details?: string): EventDetails => {
    if (!details) return {};
    try {
      return JSON.parse(details);
    } catch {
      return {};
    }
  };

  const eventTypes = [
    { value: 'event', label: 'Event', icon: <TargetIcon size={22} /> },
    { value: 'hackathon', label: 'Hackathon', icon: <CodeIcon size={22} /> },
    { value: 'conference', label: 'Conference', icon: <MicIcon size={22} /> },
    { value: 'workshop', label: 'Workshop', icon: <WrenchIcon size={22} /> },
    { value: 'exhibition', label: 'Exhibition', icon: <TargetIcon size={22} /> },
    { value: 'seminar', label: 'Seminar', icon: <BookIcon size={22} /> },
    { value: 'competition', label: 'Competition', icon: <TrophyIcon size={22} /> },
    { value: 'other', label: 'Other', icon: <TargetIcon size={22} /> }
  ] as const;

  // Fetch event data on mount
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/events/${eventId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const eventData = response.data || {};
        setEvent(eventData);
        const normalizedType = normalizeEventType(eventData.eventType || eventData.type);
        const normalizedDate = toDateTimeLocal(eventData.startDate || eventData.date);
        const details = parseDetails(eventData.details);
        
        // Populate form with existing event data
        setFormData({
          title: eventData.title || eventData.name || '',
          description: eventData.description || '',
          eventType: normalizedType,
          clubId: eventData.clubId || eventData.club_id || '',
          clubName: eventData.clubName || eventData.club_name || '',
          location: details.location || eventData.location || '',
          image: eventData.image || '',
          startDate: normalizedDate,
          endDate: toDateTimeLocal(details.endDate || eventData.endDate || eventData.date),
          registrationDeadline: toDateTimeLocal(details.registrationDeadline || eventData.registrationDeadline || eventData.date),
          maxParticipants: Number(details.maxParticipants || eventData.maxParticipants) || 100,
          organizersContactNo: details.organizersContactNo || eventData.organizersContactNo || '',
          organizersEmail: details.organizersEmail || eventData.organizersEmail || '',
          eligibility: details.eligibility || eventData.eligibility || '',
          tags: details.tags || eventData.tags || [],
          teamSize: Number(details.teamSize || eventData.teamSize) || 4,
          prizePool: details.prizePool || eventData.prizePool || '',
          timeline: details.timeline || eventData.timeline || [],
          customFields: details.customFields || [],
        });
        
        setSelectedEventType(normalizedType);
        if (eventData.image) {
          setImagePreview(eventData.image);
        }
      } catch (error) {
        console.error('Error fetching event:', error);
        alert('Failed to load event data');
        navigate('/dashboard/club/events');
      } finally {
        setLoading(false);
      }
    };

    if (eventId) {
      fetchEvent();
    }
  }, [eventId, token, API_URL, navigate]);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const uploadedUrl = await uploadImageToCdn(file, token, 'events');
        setImagePreview(uploadedUrl);
        setFormData({ ...formData, image: uploadedUrl });
      } catch (error: any) {
        alert(error?.message || 'Failed to upload image');
      }
    }
  };

  const addTag = () => {
    if (newTag.trim() && formData.tags.length < 5) {
      setFormData({
        ...formData,
        tags: [...formData.tags, newTag.trim()]
      });
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(t => t !== tag)
    });
  };

  const addTimelinePhase = () => {
    if (timelinePhase.phase && timelinePhase.date && timelinePhase.description) {
      const updatedTimeline = [...formData.timeline, timelinePhase];
      setFormData({ ...formData, timeline: updatedTimeline });
      setTimelinePhase({ phase: '', date: '', description: '' });
    }
  };

  const removeTimelinePhase = (index: number) => {
    setFormData({
      ...formData,
      timeline: formData.timeline.filter((_, i) => i !== index)
    });
  };

  const moveTimelinePhase = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || toIndex >= formData.timeline.length) {
      return;
    }
    const updated = [...formData.timeline];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    setFormData({ ...formData, timeline: updated });
  };

  const handleTimelineDrop = (toIndex: number) => {
    if (draggedTimelineIndex === null) return;
    moveTimelinePhase(draggedTimelineIndex, toIndex);
    setDraggedTimelineIndex(null);
  };

  const addCustomField = () => {
    if (!customFieldDraft.headline.trim() || !customFieldDraft.value.trim()) {
      return;
    }
    setFormData({
      ...formData,
      customFields: [...formData.customFields, { ...customFieldDraft }],
    });
    setCustomFieldDraft({ headline: '', value: '' });
  };

  const removeCustomField = (index: number) => {
    const customFields = formData.customFields.filter((_, i) => i !== index);
    setFormData({ ...formData, customFields });
  };

  const handleEventTypeSelect = (type: EventType) => {
    setSelectedEventType(type);
    setFormData({ ...formData, eventType: type });
  };

  const handleNext = () => {
    if (step === 1) {
      if (formData.title && formData.description && selectedEventType) {
        setStep(2);
      } else {
        alert('Please fill in all required fields');
      }
    } else if (step === 2) {
      if (formData.location && formData.startDate && formData.endDate && formData.registrationDeadline) {
        setStep(3);
      } else {
        alert('Please fill in all required fields');
      }
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard/club/events');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.organizersContactNo || !formData.organizersEmail || !formData.eligibility) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      const details = {
        location: formData.location,
        startDate: formData.startDate,
        endDate: formData.endDate,
        registrationDeadline: formData.registrationDeadline,
        maxParticipants: formData.maxParticipants,
        organizersContactNo: formData.organizersContactNo,
        organizersEmail: formData.organizersEmail,
        eligibility: formData.eligibility,
        tags: formData.tags,
        teamSize: formData.teamSize,
        prizePool: formData.prizePool,
        timeline: formData.timeline,
        customFields: formData.customFields,
      };

      const payload = {
        name: formData.title,
        description: formData.description,
        type: formData.eventType,
        club_id: formData.clubId || event?.club_id || event?.clubId || '',
        club_name: formData.clubName || event?.club_name || event?.clubName || '',
        fee: Number(event?.fee) || 0,
        date: formData.startDate,
        image: formData.image,
        details: JSON.stringify(details),
      };

      await axios.put(
        `${API_URL}/api/events/${eventId}`,
        payload,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      // Redirect to public event view after update
      navigate(`/public/events/${eventId}`);
      alert('Event updated successfully!');
    } catch (error: any) {
      console.error('Error updating event:', error);
      alert(error.response?.data?.message || 'Failed to update event');
    }
  };

  if (loading) {
    return (
      <div className="event-edit-loading">
        Loading event data...
      </div>
    );
  }

  return (
    <div className="event-edit-container">
      <div className="event-edit-wrapper">
        <div className="event-edit-header">
          <h1>EDIT EVENT</h1>
          <p>Modify event details and save changes</p>
        </div>

        {/* Progress Steps */}
      <div className="progress-steps">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`step ${step === s ? 'active' : ''} ${s < step ? 'completed' : ''}`}
          >
            <span>{s}</span>
            <span className="step-label">
              {s === 1 ? 'Type & Details' : s === 2 ? 'Event Info' : 'Contact & Eligibility'}
            </span>
          </div>
        ))}
      </div>

      {/* Step 1: Event Type & Basic Details */}
      <AnimatePresence mode="wait">
        {step === 1 && (
          <motion.div
            key="step1"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="form-section"
          >
            <h2>Event Type & Basic Details</h2>

            {/* Event Type Selection */}
            <div className="event-type-grid">
              {eventTypes.map((type) => (
                <motion.button
                  key={type.value}
                  className={`event-type-btn ${selectedEventType === type.value ? 'selected' : ''}`}
                  onClick={() => handleEventTypeSelect(type.value)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <span className="icon">{type.icon}</span>
                  <span className="label">{type.label}</span>
                </motion.button>
              ))}
            </div>

            {/* Basic Details Form */}
            <div className="form-group">
              <label>Event Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter event title"
              />
            </div>

            <div className="form-group">
              <label>Description *</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter event description"
                rows={4}
              />
            </div>

            <div className="form-group">
              <label>Event Image</label>
              <div className="image-upload">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  id="image-input"
                />
                <label htmlFor="image-input" className="upload-label">
                  {imagePreview ? 'Change Image' : 'Upload Image'}
                </label>
              </div>
              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="Preview" />
                </div>
              )}
            </div>

            <div className="form-actions">
              <motion.button
                onClick={handleCancel}
                className="btn btn-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                CANCEL
              </motion.button>
              <motion.button
                onClick={handleNext}
                className="btn btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                NEXT
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Step 2: Event Information */}
        {step === 2 && (
          <motion.div
            key="step2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="form-section"
          >
            <h2>Event Information</h2>

            <div className="form-row">
              <div className="form-group">
                <label>Associated Club</label>
                <input
                  type="text"
                  value={formData.clubName || 'No associated club'}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label>Club ID (Auto)</label>
                <input
                  type="text"
                  value={formData.clubId || 'No associated club id'}
                  readOnly
                />
              </div>
            </div>

            <div className="form-group">
              <label>Location *</label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Enter event location"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Start Date *</label>
                <input
                  type="datetime-local"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>End Date *</label>
                <input
                  type="datetime-local"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Registration Deadline *</label>
                <input
                  type="datetime-local"
                  value={formData.registrationDeadline}
                  onChange={(e) => setFormData({ ...formData, registrationDeadline: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Max Participants</label>
                <input
                  type="number"
                  value={formData.maxParticipants}
                  onChange={(e) => {
                    const value = Number(e.target.value);
                    setFormData({ ...formData, maxParticipants: Number.isNaN(value) ? 1 : value });
                  }}
                  min="1"
                />
              </div>
            </div>

            {/* Hackathon Specific Fields */}
            {formData.eventType === 'hackathon' && (
              <div className="form-row">
                <div className="form-group">
                  <label>Team Size</label>
                  <input
                    type="number"
                    value={formData.teamSize}
                    onChange={(e) => {
                      const value = Number(e.target.value);
                      setFormData({ ...formData, teamSize: Number.isNaN(value) ? 1 : value });
                    }}
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Prize Pool</label>
                  <input
                    type="text"
                    value={formData.prizePool}
                    onChange={(e) => setFormData({ ...formData, prizePool: e.target.value })}
                    placeholder="e.g., ₹50000"
                  />
                </div>
              </div>
            )}

            {/* Timeline (available for all event types) */}
            <div className="form-section-title">Timeline (Optional)</div>
            {formData.timeline.length > 0 && (
              <div className="timeline-list">
                {formData.timeline.map((item, idx) => (
                  <motion.div
                    key={`${item.phase}-${item.date}-${idx}`}
                    className="timeline-item"
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    draggable
                    onDragStart={() => setDraggedTimelineIndex(idx)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => handleTimelineDrop(idx)}
                    onDragEnd={() => setDraggedTimelineIndex(null)}
                    style={{
                      cursor: 'grab',
                      opacity: draggedTimelineIndex === idx ? 0.6 : 1,
                    }}
                  >
                    <div className="timeline-content">
                      <strong>{item.phase}</strong>
                      <p>{item.date}</p>
                      <p style={{ whiteSpace: 'pre-wrap' }}>{item.description}</p>
                      <p style={{ marginTop: '0.35rem', fontSize: '0.75rem', opacity: 0.7 }}>
                        Drag to reorder
                      </p>
                    </div>
                    <div className="timeline-controls">
                      <button
                        type="button"
                        className="timeline-move-btn"
                        onClick={() => moveTimelinePhase(idx, idx - 1)}
                        disabled={idx === 0}
                      >
                        UP
                      </button>
                      <button
                        type="button"
                        className="timeline-move-btn"
                        onClick={() => moveTimelinePhase(idx, idx + 1)}
                        disabled={idx === formData.timeline.length - 1}
                      >
                        DOWN
                      </button>
                      <button
                        onClick={() => removeTimelinePhase(idx)}
                        className="remove-btn"
                        type="button"
                      >
                        <CloseIcon size={12} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            <div className="timeline-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Phase Name</label>
                  <input
                    type="text"
                    value={timelinePhase.phase}
                    onChange={(e) => setTimelinePhase({ ...timelinePhase, phase: e.target.value })}
                    placeholder="e.g., Registration"
                  />
                </div>
                <div className="form-group">
                  <label>Date</label>
                  <input
                    type="datetime-local"
                    value={timelinePhase.date}
                    onChange={(e) => setTimelinePhase({ ...timelinePhase, date: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={timelinePhase.description}
                  onChange={(e) => setTimelinePhase({ ...timelinePhase, description: e.target.value })}
                  placeholder="Enter phase description"
                  rows={2}
                />
              </div>
              <motion.button
                onClick={addTimelinePhase}
                className="btn btn-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                + Add Phase
              </motion.button>
            </div>

            <div className="form-actions">
              <motion.button
                onClick={handleBack}
                className="btn btn-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                BACK
              </motion.button>
              <motion.button
                onClick={handleNext}
                className="btn btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                NEXT
              </motion.button>
            </div>
          </motion.div>
        )}

        {/* Step 3: Contact & Eligibility */}
        {step === 3 && (
          <motion.div
            key="step3"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="form-section"
          >
            <h2>Contact & Eligibility</h2>

            <div className="form-row">
              <div className="form-group">
                <label>Organizer Contact Number *</label>
                <input
                  type="tel"
                  value={formData.organizersContactNo}
                  onChange={(e) => setFormData({ ...formData, organizersContactNo: e.target.value })}
                  placeholder="Enter contact number"
                />
              </div>
              <div className="form-group">
                <label>Organizer Email *</label>
                <input
                  type="email"
                  value={formData.organizersEmail}
                  onChange={(e) => setFormData({ ...formData, organizersEmail: e.target.value })}
                  placeholder="Enter email"
                />
              </div>
            </div>

            <div className="form-group">
              <label>Eligibility Criteria *</label>
              <textarea
                value={formData.eligibility}
                onChange={(e) => setFormData({ ...formData, eligibility: e.target.value })}
                placeholder="Enter eligibility criteria"
                rows={4}
              />
            </div>

            {/* Tags Section */}
            <div className="form-group">
              <label>Tags (Max 5)</label>
              <div className="tags-input">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  placeholder="Add tag and press Enter"
                />
                <motion.button
                  onClick={addTag}
                  className="add-tag-btn"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  + Add
                </motion.button>
              </div>
              <div className="tags-list">
                {formData.tags.map((tag, idx) => (
                  <motion.span
                    key={idx}
                    className="tag"
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                  >
                    {tag}
                    <button onClick={() => removeTag(tag)}>×</button>
                  </motion.span>
                ))}
              </div>
            </div>

            {/* Dynamic Custom Fields */}
            <div className="form-group">
              <label>Custom Fields (Headline + Value)</label>
              <div className="form-row">
                <div className="form-group">
                  <input
                    type="text"
                    value={customFieldDraft.headline}
                    onChange={(e) => setCustomFieldDraft({ ...customFieldDraft, headline: e.target.value })}
                    placeholder="Headline (e.g., Problem Statement)"
                  />
                </div>
                <div className="form-group">
                  <textarea
                    value={customFieldDraft.value}
                    onChange={(e) => setCustomFieldDraft({ ...customFieldDraft, value: e.target.value })}
                    placeholder="Value"
                    rows={3}
                  />
                </div>
              </div>
              <motion.button
                onClick={addCustomField}
                className="add-tag-btn"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                + Add Field
              </motion.button>
              {(formData.customFields.length > 0) && (
                <div className="tags-list">
                  {formData.customFields.map((item, idx) => (
                    <motion.span
                      key={idx}
                      className="tag"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      style={{ whiteSpace: 'pre-wrap' }}
                    >
                      <strong>{item.headline}:</strong>&nbsp;{item.value}
                      <button onClick={() => removeCustomField(idx)}>×</button>
                    </motion.span>
                  ))}
                </div>
              )}
            </div>

            <div className="form-actions">
              <motion.button
                onClick={handleBack}
                className="btn btn-secondary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                BACK
              </motion.button>
              <motion.button
                onClick={handleSubmit}
                className="btn btn-primary"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                SAVE CHANGES
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default EventEditPage;
